import React, { useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Youtube from '@tiptap/extension-youtube';
import {
  Bold, Italic, Strikethrough, Heading1, Heading2, Heading3, List, ListOrdered,
  Quote, Code, Undo, Redo, Image as ImageIcon, Youtube as YoutubeIcon, Link2, Link2Off,
  Minus,
} from 'lucide-react';
import { uploadFile } from '../services/api';

const Btn = ({ active, onClick, disabled, title, children, testid }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testid}
    className={`h-8 w-8 inline-flex items-center justify-center rounded-md text-sm transition ${
      active ? 'bg-[#0f5c3d] text-white' : 'text-[#0b1a2e] hover:bg-black/5'
    } disabled:opacity-30 disabled:cursor-not-allowed`}
  >
    {children}
  </button>
);

const Toolbar = ({ editor }) => {
  const insertImage = useCallback(async () => {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = 'image/*';
    inp.onchange = async () => {
      const file = inp.files?.[0];
      if (!file) return;
      try {
        const { url } = await uploadFile(file);
        const absolute = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
        editor.chain().focus().setImage({ src: absolute, alt: file.name }).run();
      } catch (e) {
        alert('Image upload failed');
      }
    };
    inp.click();
  }, [editor]);

  const insertYouTube = useCallback(() => {
    const url = window.prompt('Paste YouTube URL:');
    if (!url) return;
    editor.chain().focus().setYoutubeVideo({ src: url, width: 640, height: 360 }).run();
  }, [editor]);

  const toggleLink = useCallback(() => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt('URL:');
    if (!url) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="flex flex-wrap gap-1 border-b border-[#0f5c3d]/15 bg-[#f7fbf8] px-2 py-1.5 rounded-t-lg">
      <Btn testid="editor-h1" active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1"><Heading1 className="w-4 h-4" /></Btn>
      <Btn testid="editor-h2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2"><Heading2 className="w-4 h-4" /></Btn>
      <Btn testid="editor-h3" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3"><Heading3 className="w-4 h-4" /></Btn>
      <span className="w-px bg-black/10 mx-1 my-1" />
      <Btn testid="editor-bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold"><Bold className="w-4 h-4" /></Btn>
      <Btn testid="editor-italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic"><Italic className="w-4 h-4" /></Btn>
      <Btn testid="editor-strike" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough"><Strikethrough className="w-4 h-4" /></Btn>
      <Btn testid="editor-code" active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} title="Code"><Code className="w-4 h-4" /></Btn>
      <span className="w-px bg-black/10 mx-1 my-1" />
      <Btn testid="editor-ul" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list"><List className="w-4 h-4" /></Btn>
      <Btn testid="editor-ol" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list"><ListOrdered className="w-4 h-4" /></Btn>
      <Btn testid="editor-quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Blockquote"><Quote className="w-4 h-4" /></Btn>
      <Btn testid="editor-hr" onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider"><Minus className="w-4 h-4" /></Btn>
      <span className="w-px bg-black/10 mx-1 my-1" />
      <Btn testid="editor-img" onClick={insertImage} title="Insert image"><ImageIcon className="w-4 h-4" /></Btn>
      <Btn testid="editor-yt" onClick={insertYouTube} title="Insert YouTube video"><YoutubeIcon className="w-4 h-4" /></Btn>
      <Btn testid="editor-link" active={editor.isActive('link')} onClick={toggleLink} title="Toggle link">
        {editor.isActive('link') ? <Link2Off className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
      </Btn>
      <span className="w-px bg-black/10 mx-1 my-1" />
      <Btn testid="editor-undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><Undo className="w-4 h-4" /></Btn>
      <Btn testid="editor-redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><Redo className="w-4 h-4" /></Btn>
    </div>
  );
};

const RichTextEditor = ({ value, onChange, placeholder = 'Start writing…' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Image.configure({ HTMLAttributes: { class: 'rounded-lg my-4' } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: 'text-[#0f5c3d] underline' } }),
      Placeholder.configure({ placeholder }),
      Youtube.configure({ nocookie: true, HTMLAttributes: { class: 'my-4 aspect-video w-full rounded-lg' } }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-slate max-w-none min-h-[400px] px-4 py-3 focus:outline-none tiptap-body',
        'data-testid': 'rich-text-editor',
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    // Sync external value → editor when the parent supplies a new value (e.g. loading edit)
    if (value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  return (
    <div className="bg-white border border-[#0f5c3d]/15 rounded-lg overflow-hidden">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
